import { Box, Container, Paper, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Breadcrumbs } from '../../../components/shared';
import useStyles from './style';
import BREADCRUMB_NAMES from '../../../constants/breadcrumbNames';
import { routeConstants } from '../../../constants/routeConstants';
import { leaseDetailsFormAction, resetLeaseAction, rentDetailsFormAction } from '../../../redux-slice/lease';
import { useDispatch, useSelector } from 'react-redux';
import RentDetailsForm from '../../../components/RentDetailsForm';
import { dateFormatForApi } from '../../../utils/common-methods';
import { useAddLeaseMutation } from '../../../services/Lease/LeaseService';
import { RENT_TYPE_CONFIG } from '../../../constants/rentDetailsConfig';

const AddNewLease = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const locationId = searchParams.get('location');
  const subLocationId = searchParams.get('subLocation');
  const {
    LEASE: { LEASE, ADD_LEASE },
    LOCATION: { LOCATION, LOCATION_DETAIL, SUB_LOCATION_DETAIL },
  } = BREADCRUMB_NAMES;
  const leaseBreadcrumbData = [{ text: LEASE, redirection: `${routeConstants.LEASE_ROUTE}` }, { text: ADD_LEASE }];
  const locationBreadcrumbData = [
    { text: LOCATION, redirection: `${routeConstants.LOCATION_ROUTE}` },
    { text: LOCATION_DETAIL, redirection: `${routeConstants.LOCATION_ROUTE}/${locationId}` },
    {
      text: SUB_LOCATION_DETAIL,
      redirection: `${routeConstants.SUBLOCATION_ROUTE}/${subLocationId}?locationId=${locationId}`,
    },
    { text: ADD_LEASE },
  ];
  const [currentIndex, setCurrentIndex] = useState(1);
  const dispatch = useDispatch();
  const leaseFormsValue = useSelector((state) => state?.lease?.leaseForms);
  const { leaseDetails } = leaseFormsValue;
  const [AddNewLeaseApi, { isLoading }] = useAddLeaseMutation();
  // eslint-disable-next-line no-unused-vars
  const leaseDetailsFormRef = useRef(); // Create a ref for LeaseDetailsForm

  useEffect(() => {
    const unloadCallback = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', unloadCallback);
    return () => {
      window.removeEventListener('beforeunload', unloadCallback);
      dispatch(resetLeaseAction());
    };
  }, []);

  // this is for when page(component) loads all states should be empty.
  useEffect(() => {
    if (!locationId && !subLocationId) dispatch(resetLeaseAction());
  }, []);

  // eslint-disable-next-line no-unused-vars
  const onNextClickHandler = (type, values) => {
    if (type === 'leaseDetails') {
      const formValues = {
        ...values,
        leaseBegins: values.leaseBegins.toString(),
        leaseEnds: values.leaseEnds.toString(),
      };
      dispatch(leaseDetailsFormAction(formValues));
    }
    if (type === 'rentDetails') {
      dispatch(rentDetailsFormAction(values));
    }
    const nextIndex = currentIndex + 1;
    setCurrentIndex(nextIndex);
  };

  const onCancelClickHandler = () => {
    navigate(`/${routeConstants.LEASE_ROUTE}`, { redirect: true });
  };

  const rentDetailsObj = (rentDetails) => {
    const gameCategoryRates =
      rentDetails?.modalTypes?.map((value) => ({
        gameCategoryId: value?.id,
        rate: value?.rate,
      })) || [];

    // Return the object with gameCategoryRates only if it has elements
    return gameCategoryRates && gameCategoryRates.length > 0 ? { gameCategoryRates } : null;
  };

  const onAddClickHandler = (values) => {
    const subLocationIds = leaseDetails?.subLocation?.map((value) => value?.id);
    const leaseObj = {
      name: leaseDetails.leaseName,
      locationId: leaseDetails?.location?.id,
      sublocationIds: subLocationIds,
      leaseType: leaseDetails?.leaseType,
      status: leaseDetails?.status,
      termStartDate: dateFormatForApi(leaseDetails?.leaseBegins),
      termEndDate: dateFormatForApi(leaseDetails?.leaseEnds),
    };

    const rentDetailsCommon = {
      rentType: values?.rentType,
      fee: RENT_TYPE_CONFIG.rentFee.includes(values?.rentType) ? values?.rentFee : null,
      rate: RENT_TYPE_CONFIG.rentRate.includes(values?.rentType) ? values?.rentRate : null,
      CAMFee: RENT_TYPE_CONFIG.CAMFee.includes(values?.rentType) ? values?.CAMFee : null,
      CAMRate: RENT_TYPE_CONFIG.CAMRate.includes(values?.rentType) ? values?.CAMRate : null,
      breakpointRevenue: RENT_TYPE_CONFIG.breakpointRevenue.includes(values?.rentType)
        ? values?.breakpointRevenue
        : null,
      breakpointRate: RENT_TYPE_CONFIG.breakpointRate.includes(values?.rentType) ? values?.breakpointRate : null,
      resetTerm: RENT_TYPE_CONFIG.resetTerm.includes(values?.rentType) ? values?.resetTerm : null,
      payFrequency: RENT_TYPE_CONFIG.payFrequency.includes(values?.rentType) ? values?.payFrequency : null,
      notes: values?.notes || '',
      contractLink: values?.contractLink || '',
      coinCost: values?.coinCost ?? null,
      cardCost: values?.cardCost ?? null,
      tierBreakpointAndRate:
        values?.tierSummaryState
          ?.filter((item) => item?.tierThreshold > 0 && item?.tierRate > 0) // Filter items where both are greater than 0
          .map((item) => ({
            tierThreshold: item.tierThreshold,
            tierRate: item.tierRate,
          })) || [], // Fallback to an empty array if values?.tierSummaryState is undefined
    };
    const conditionWiseRentDetailsObj = rentDetailsObj(values);
    const finalRentDetailsObj = {
      ...rentDetailsCommon,
      ...(conditionWiseRentDetailsObj || {}), // Safely spread conditionWiseRentDetailsObj (or an empty object if null)
    };

    const finalObj = {
      ...leaseObj,
      ...finalRentDetailsObj,
    };
    AddNewLeaseApi(finalObj).then((res) => {
      if (res.data) {
        if (locationId && subLocationId) {
          navigate(`/${routeConstants.SUBLOCATION_ROUTE}/${subLocationId}?locationId=${locationId}`, {
            replace: true,
          });
        } else {
          navigate(`/${routeConstants.LEASE_ROUTE}?selectedTab=1`, {
            replace: true,
          });
        }
      }
    });
  };

  return (
    <Container maxWidth="xl" className={classes.root}>
      <Box mb={3}>
        <Box>
          <Breadcrumbs
            variant="body2"
            underline="hover"
            data={locationId && subLocationId ? locationBreadcrumbData : leaseBreadcrumbData}
          />
        </Box>
        <Box>
          <Typography variant="h1">Add New Lease</Typography>
        </Box>
      </Box>
      <Box component={Paper}>
        <Box mt={1} mx={4}>
          <RentDetailsForm onAddClick={onAddClickHandler} onCancelClick={onCancelClickHandler} isLoading={isLoading} />
        </Box>
      </Box>
    </Container>
  );
};

export default AddNewLease;
